import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Fab,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';

const StaffManagement = () => {
  // API base URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    bio: '',
    whatsapp: '',
    email: '',
    is_active: true
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  // Fetch staff data
  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/staff`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStaff(data.staff || []);
      } else {
        setError('Failed to fetch staff data');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching staff:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        setError('File size must be less than 2MB');
        return;
      }

      setSelectedFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Upload profile picture
  const uploadProfilePicture = async (staffId) => {
    if (!selectedFile) return null;

    setUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('image', selectedFile);

      const response = await fetch(`${API_BASE_URL}/staff/${staffId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        return result.url;
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (err) {
      setError('Failed to upload profile picture');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('access_token');
      const url = editingStaff 
        ? `${API_BASE_URL}/staff/${editingStaff.id}`
        : `${API_BASE_URL}/staff`;
      
      const method = editingStaff ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Upload profile picture if selected
        if (selectedFile) {
          try {
            await uploadProfilePicture(result.id);
          } catch (uploadErr) {
            // Staff created/updated but image upload failed
            setError('Staff saved but profile picture upload failed');
          }
        }

        setSuccess(editingStaff ? 'Staff updated successfully' : 'Staff created successfully');
        setOpenDialog(false);
        resetForm();
        fetchStaff(); // Refresh the list
      } else {
        const errorText = await response.text();
        setError(`Failed to ${editingStaff ? 'update' : 'create'} staff: ${errorText}`);
      }
    } catch (err) {
      setError(`Error ${editingStaff ? 'updating' : 'creating'} staff`);
      console.error('Error submitting form:', err);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/staff/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('Staff deleted successfully');
        fetchStaff(); // Refresh the list
      } else {
        setError('Failed to delete staff');
      }
    } catch (err) {
      setError('Error deleting staff');
      console.error('Error deleting staff:', err);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      bio: '',
      whatsapp: '',
      email: '',
      is_active: true
    });
    setEditingStaff(null);
    setSelectedFile(null);
    setPreviewUrl('');
  };

  // Open dialog for editing
  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      name: staffMember.name,
      role: staffMember.role,
      bio: staffMember.bio || '',
      whatsapp: staffMember.whatsapp,
      email: staffMember.email || '',
      is_active: staffMember.is_active
    });
    setPreviewUrl(staffMember.profile_picture || '');
    setOpenDialog(true);
  };

  // Open dialog for creating new staff
  const handleAdd = () => {
    resetForm();
    setOpenDialog(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Staff Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          color="primary"
        >
          Add Staff Member
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Staff Grid View */}
      <Grid container spacing={3}>
        {staff.map((member) => (
          <Grid item xs={12} sm={6} md={4} key={member.id}>
            <Card>
              <CardMedia
                component="div"
                sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5'
                }}
              >
                <Avatar
                  src={member.profile_picture ? `${API_BASE_URL}${member.profile_picture}` : ''}
                  sx={{ width: 120, height: 120 }}
                >
                  <PersonIcon sx={{ fontSize: 60 }} />
                </Avatar>
              </CardMedia>
              <CardContent>
                <Typography variant="h6" component="h2" gutterBottom>
                  {member.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {member.role}
                </Typography>
                {member.bio && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {member.bio.length > 100 ? `${member.bio.substring(0, 100)}...` : member.bio}
                  </Typography>
                )}
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PhoneIcon fontSize="small" />
                  <Typography variant="body2">{member.whatsapp}</Typography>
                </Box>
                {member.email && (
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <EmailIcon fontSize="small" />
                    <Typography variant="body2">{member.email}</Typography>
                  </Box>
                )}
                <Chip 
                  label={member.is_active ? 'Active' : 'Inactive'} 
                  color={member.is_active ? 'success' : 'default'}
                  size="small"
                />
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEdit(member)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDelete(member.id)}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Profile Picture Upload */}
              <Grid item xs={12} md={4}>
                <Box textAlign="center">
                  <Avatar
                    src={previewUrl}
                    sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                  >
                    <PersonIcon sx={{ fontSize: 60 }} />
                  </Avatar>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="profile-picture-upload"
                    type="file"
                    onChange={handleFileSelect}
                  />
                  <label htmlFor="profile-picture-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCameraIcon />}
                      fullWidth
                    >
                      Upload Photo
                    </Button>
                  </label>
                  {selectedFile && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {selectedFile.name}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Form Fields */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="name"
                      label="Full Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="role"
                      label="Role/Position"
                      value={formData.role}
                      onChange={handleInputChange}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="whatsapp"
                      label="WhatsApp Number"
                      value={formData.whatsapp}
                      onChange={handleInputChange}
                      fullWidth
                      required
                      placeholder="+226XXXXXXXX"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="email"
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="bio"
                      label="Biography"
                      value={formData.bio}
                      onChange={handleInputChange}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Brief description of the staff member..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleInputChange}
                        />
                      }
                      label="Active Staff Member"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} startIcon={<CancelIcon />}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              startIcon={uploading ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={uploading}
            >
              {uploading ? 'Saving...' : (editingStaff ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default StaffManagement;
